import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Conversation} from '@app/core/models/conversation.model';
import {map} from 'rxjs/operators';
import {HttpService} from '@app/core/services/http.service';
import {Message} from '@app/core/models/message.model';
import {BaseService} from '@app/core/services/base.service';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Pagination, PaginationHolder} from '@app/core/models/pagination.model';

export interface ICreateMessageParams {
	content?: string;
	shift_id?: number;
	file_ids?: number[];
	shift?: {
		id: number,
	};
}

@Injectable({
	providedIn: 'root',
})
export class MessagesService extends BaseService {

	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public freshConversation(params?): Conversation {
		if (!params) {
			return new Conversation(this.tenantTZ, this.interService);
		}

		return new Conversation(this.tenantTZ, this.interService).deserialize(params);
	}

	public freshMessage(params?): Message {
		if (!params) {
			return new Message(this.tenantTZ, this.interService);
		}

		return new Message(this.tenantTZ, this.interService).deserialize(params);
	}

	public getConversations(): Observable<Conversation[]> {
		return this.httpService.getJson<Conversation[]>('conversations', {
			include: 'group.department,unread_notifications_count',
		}).pipe(map(conversations => {
			conversations.forEach(c => {
				if (c.is_personal_conversation) {
					let userDeactivated = false;
					c.users.forEach(u => {
						if (u.deactivated_at) {
							userDeactivated = true;
						}
					});
					c['deactivated'] = userDeactivated;
				}
			});
			conversations = conversations.map(c => this.freshConversation(c));

			return conversations;
		}));
	}

	public getMessages(id: number, page_size: number = 50, page: number = 1): Observable<PaginationHolder<Message>> {
		const data = {
			page_size,
			page,
		};

		return this.httpService.getJson<PaginationHolder<Message>>('conversations/' + id + '/messages', data).pipe(map((res: any) => {
			res.data = res.data.map(m => this.freshMessage(m));
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public createMessage(id: number, data: ICreateMessageParams): Observable<Message> {
		return this.httpService.postJson('conversations/' + id + '/messages', data).pipe(map((message: Message) =>
			this.freshMessage(message)));
	}

	public createConversation(users: any, conversationName?: string): Observable<Conversation> {
		const dataToPost = {
			conversation_name: conversationName,
			users,
		};

		return this.httpService.postJson('conversations', dataToPost).pipe(map((conversation: Conversation) =>
			this.freshConversation(conversation)));
	}

	public leaveConversation(id: number): Observable<any> {
		return this.httpService.deleteJson('conversations/' + id);
	}

	public renameConversation(data: any, id: number): Observable<Conversation> {
		return this.httpService.patchJson('conversations/' + id, data).pipe(map((conversation: Conversation) =>
			this.freshConversation(conversation)));
	}
}
